import { useSyncExternalStore } from 'react';
import { toast } from 'sonner';

/**
 * ONE audio element for the whole app, so starting a sound anywhere stops
 * whatever was playing — a page of fifty sound links must never become fifty
 * overlapping previews. A module-level singleton rather than a context: sound
 * links render in dialogs, menus and pages outside the Discovery tree, and
 * none of them should need a provider to share it.
 *
 * TikTok preview URLs are direct, CORS-open CDN audio, so a plain <audio>
 * plays them; no proxy.
 */

export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

interface PlayerState {
  /** Which sound the element holds (a SoundRef key), if any. */
  key: string | null;
  status: PlayerStatus;
}

let state: PlayerState = { key: null, status: 'idle' };
const listeners = new Set<() => void>();
let audio: HTMLAudioElement | null = null;

function set(next: PlayerState): void {
  state = next;
  for (const l of listeners) l();
}

function failed(key: string): void {
  if (state.key !== key) return;
  set({ key, status: 'error' });
  // One toast however many ways the failure is reported (rejected play() and the error event).
  toast.error("Couldn't play this sound", {
    id: 'sound-preview-error',
    description: 'The preview may be unavailable — open the sound on TikTok instead.',
  });
}

function element(): HTMLAudioElement {
  if (audio) return audio;
  const el = new Audio();
  el.preload = 'none';
  el.addEventListener('playing', () => {
    if (state.key) set({ key: state.key, status: 'playing' });
  });
  el.addEventListener('waiting', () => {
    if (state.key && state.status === 'playing') set({ key: state.key, status: 'loading' });
  });
  el.addEventListener('ended', () => set({ key: null, status: 'idle' }));
  el.addEventListener('error', () => {
    if (state.key && el.getAttribute('src')) failed(state.key);
  });
  audio = el;
  return el;
}

/**
 * Play `src` as sound `key`, or pause it if it is the one playing. Starting a
 * different sound stops the current one first.
 */
export async function toggleSound(key: string, src: string): Promise<void> {
  const el = element();
  if (state.key === key && (state.status === 'playing' || state.status === 'loading')) {
    el.pause();
    set({ key, status: 'paused' });
    return;
  }
  if (state.key !== key) {
    el.pause();
    el.src = src;
  }
  set({ key, status: 'loading' });
  try {
    await el.play();
    if (state.key === key && state.status === 'loading') set({ key, status: 'playing' });
  } catch (err) {
    // Superseded by another sound, or paused before it started: not a failure.
    if (state.key !== key || (err as { name?: string })?.name === 'AbortError') return;
    failed(key);
  }
}

/** Stop whatever is playing (e.g. when the page that started it goes away). */
export function stopSound(): void {
  audio?.pause();
  if (state.key) set({ key: null, status: 'idle' });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
const snapshot = (): PlayerState => state;

/** This sound's state in the shared player. */
export function useSoundPlayer(key: string): { status: PlayerStatus; toggle: (src: string) => Promise<void> } {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  return { status: s.key === key ? s.status : 'idle', toggle: (src) => toggleSound(key, src) };
}

/** Test hook: forget the element and state between tests. */
export function resetSoundPlayerForTests(): void {
  audio?.pause();
  audio = null;
  state = { key: null, status: 'idle' };
  for (const l of listeners) l();
}
